/**
 * Canonical entity-profile URL builder — the ONE place in the frontend that
 * knows where an entity's public profile lives.
 *
 * Today the canonical entity surface is `/tags/[slug]/`. The rename to
 * `/entities/[slug]/` is CERTAIN but deferred to its own discrete post-push
 * event alongside the domain cutover (Lam's decision, 2026-06-10). When that
 * event lands, flip ENTITY_PATH_SEGMENT to 'entities' and every helper-derived
 * URL follows — a one-line migration.
 *
 * Decision records:
 *   - docs/superpowers/plans/2026-06-10-tags-migration-panel-synthesis.md
 *   - docs/superpowers/plans/2026-06-10-tags-to-dashboard-migration.md
 *
 * Backend mirror (same contract, CJS): backend/src/utils/entity-url.js —
 * keep the two in sync.
 *
 * NOT covered by this helper (stay literal, see the migration runbook):
 *   - redirect config (next.config.js, middleware.ts, vercel.json)
 *   - the entity-directory index URL `/tags/` (no slug)
 *   - the per-entity RSS feed `/tags/feed.xml?tag=...` (route location, not an
 *     entity profile URL)
 */

/**
 * The path segment under which entity profiles live. The future
 * /entities/[slug] migration flips this single constant to 'entities'.
 */
export const ENTITY_PATH_SEGMENT = 'tags';

export interface EntityUrlOptions {
  /**
   * Default true — emitted URLs end with '/' to match next.config.js
   * `trailingSlash: true` (no 308 hop for crawlers).
   *
   * Pass false ONLY where the call site must stay byte-identical with a
   * legacy slash-less string: hrefs that append `?query` directly, and
   * character-budgeted strings (e.g. tweet CTA lines).
   */
  trailingSlash?: boolean;
  /**
   * Dossier record-section deep link, appended as `#<hash>` — the wayfinding
   * "doors" contract ('market' | 'social' | 'timeline', = DossierRecordTabs
   * keys). A HASH, never a query param: the dossier is ISR-cached and hashes
   * are client-only, so deep links ride the cache untouched. Read on the
   * client by DossierRecordTabs; unknown hashes are a silent no-op there.
   */
  hash?: string;
}

/**
 * Relative entity-profile URL: `/tags/${slug}/`.
 */
export function entityUrl(slug: string, options?: EntityUrlOptions): string {
  const trail = options?.trailingSlash === false ? '' : '/';
  const hash = options?.hash ? `#${options.hash}` : '';
  return `/${ENTITY_PATH_SEGMENT}/${slug}${trail}${hash}`;
}

/**
 * Absolute entity-profile URL for emitters that stamp full URLs into the
 * outside world (sitemap, llms.txt, JSON-LD, AI APIs).
 *
 * @param base Site origin WITHOUT trailing slash (e.g. "https://descout.xyz").
 *             Defaults to NEXT_PUBLIC_SITE_URL || "https://descout.xyz".
 */
export function entityUrlAbsolute(
  slug: string,
  base?: string,
  options?: EntityUrlOptions
): string {
  const site = (base ?? process.env.NEXT_PUBLIC_SITE_URL ?? 'https://descout.xyz')
    .replace(/\/+$/, '');
  return `${site}${entityUrl(slug, options)}`;
}
